import { getGetApiUserListQueryOptions, type UserDTO } from '@smartsash/api-client';
import { defineDataScreen, pagedQuery } from '@smartsash/data-screen';
import { usePermission } from '@smartsash/permissions';
import { Avatar, Badge, Button, Icon } from '@smartsash/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import MobileCard from './MobileCard';
import RowActions from './RowActions';
import styles from './Users.module.css';
import { UsersFilter } from './UsersFilter';
import {
  userDisplayName,
  userKeyOf,
  usersColumnText,
  usersQueryParams,
  usersSearchSchema,
  type UsersColumnId,
} from './usersModel';

import { useDataScreenTranslate } from '@/hooks/useDataScreenTranslate';
import { useSessionUser } from '@/services/auth/session';
import { ContactLink } from '@/ui/shared/ContactLink';
import { EMPTY_VALUE } from '@/utils/emptyValue';
import { firstNonEmpty } from '@/utils/firstNonEmpty';

/** Headers render rather than resolve: a module-scope definition cannot call `t`. */
function ColumnHeader({ column }: { column: UsersColumnId }) {
  const { t } = useTranslation();
  // The institution header is the one that does not follow `users.columns.*`.
  return (
    <>{column === 'institution' ? t('users.details.institute') : t(`users.columns.${column}`)}</>
  );
}

function CertificationCell({ row }: { row: UserDTO }) {
  const { t } = useTranslation();

  return row.isCertified === true ? (
    <Badge variant="modern" size="sm" color="success" iconLeading={<Icon name="check" />}>
      {t('users.certified')}
    </Badge>
  ) : (
    <Badge variant="modern" size="sm" color="error" iconLeading={<Icon name="x-close" />}>
      {t('users.uncertified')}
    </Badge>
  );
}

function NameCell({ row }: { row: UserDTO }) {
  const key = userKeyOf(row);

  return (
    <Link
      to="/users/$id/$tenantId"
      params={{ id: key.id, tenantId: key.tenantId }}
      className={styles['name-cell']}
    >
      <Avatar name={userDisplayName(row)} size="sm" />
      <div className={styles['name-stack']}>
        <span className={styles['name-primary']}>{userDisplayName(row)}</span>
        {row.userIdentifier != null && row.userIdentifier !== '' && (
          <span className={styles['name-code']}>{row.userIdentifier}</span>
        )}
      </div>
    </Link>
  );
}

const columns: ColumnDef<UserDTO>[] = [
  {
    id: 'name',
    header: () => <ColumnHeader column="name" />,
    cell: ({ row }) => <NameCell row={row.original} />,
    meta: { primary: true, sortable: true, value: usersColumnText.name },
  },
  {
    id: 'institution',
    header: () => <ColumnHeader column="institution" />,
    cell: ({ row }) => (
      <Badge variant="modern" size="sm" iconLeading={<Icon name="bank" />}>
        {usersColumnText.institution(row.original)}
      </Badge>
    ),
    meta: { width: '200px', value: usersColumnText.institution },
  },
  {
    // HS-1565 slice 2: the base role. No `sortable` — `GET /api/User/list` has no role sort, and
    // filtering by role already lives in the builder (`USERS_ROLE_FIELD`).
    id: 'role',
    header: () => <ColumnHeader column="role" />,
    cell: ({ row }) => {
      const role = usersColumnText.role(row.original);
      return role ? (
        <Badge variant="modern" size="sm" iconLeading={<Icon name="shield-01" />}>
          {role}
        </Badge>
      ) : (
        EMPTY_VALUE
      );
    },
    meta: { width: '160px', sortable: false, value: usersColumnText.role },
  },
  {
    id: 'email',
    header: () => <ColumnHeader column="email" />,
    cell: ({ row }) => (
      <ContactLink type="email" value={row.original.email} className={styles['link-cell']} />
    ),
    meta: { width: '220px', sortable: true, value: usersColumnText.email },
  },
  {
    id: 'certification',
    header: () => <ColumnHeader column="certification" />,
    cell: ({ row }) => <CertificationCell row={row.original} />,
    // The badge's text is translated copy, and a column reader has no translator — the filter
    // supplies the same two labels as its vocabulary, so search sees them there.
    meta: { width: '140px', sortable: true },
  },
  {
    id: 'deactivationDate',
    header: () => <ColumnHeader column="deactivationDate" />,
    cell: ({ row }) => row.original.activeUntil,
    meta: { width: '160px', sortable: true, value: usersColumnText.deactivationDate },
  },
];

/**
 * The tenant a new user goes into: the first loaded row's institution, as the page always took it.
 * Reads the list's own cached page rather than inventing a different source. Add uses it; Import
 * uses it only as a fallback (D21). It is `''` while the page loads or when it has no rows.
 */
function useListTenantId(): string {
  const search = usersSearchSchema.parse(useSearch({ strict: false }));
  const { data } = useQuery(getGetApiUserListQueryOptions(usersQueryParams(search)));
  return data?.items?.[0]?.institutionId ?? '';
}

function AddUserButton({ iconOnly = false }: { iconOnly?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenantId = useListTenantId();
  const goToNew = () => {
    void navigate({ to: '/users/new/$tenantId', params: { tenantId } });
  };

  return iconOnly ? (
    <Button
      hierarchy="primary"
      size="sm"
      iconLeading={<Icon name="plus" />}
      iconOnly
      aria-label={t('users.addAriaLabel')}
      onClick={goToNew}
    />
  ) : (
    <Button hierarchy="primary" size="sm" iconLeading={<Icon name="plus" />} onClick={goToNew}>
      {t('users.add')}
    </Button>
  );
}

/**
 * HS-1611: Import from CSV, beside Add. The import creates users **and** grants scopes, so it
 * needs both keys (D12); the header action's own gate checks only `user.create`.
 *
 * It opens under the session user's own institution (D21), which is also the wizard's default
 * target: the first list row may belong to any institution the caller can see, and taking it sent
 * admins into an institution that was not theirs. Only a session without an institution falls back
 * to that row. Hidden while neither is known: an empty `$tenantId` would open a wizard that reads
 * every lookup as "this institution has no laboratories or devices".
 */
function ImportUsersButton({ iconOnly = false }: { iconOnly?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const sessionInstitutionId = useSessionUser()?.institutionId;
  const listTenantId = useListTenantId();
  const tenantId = firstNonEmpty(sessionInstitutionId, listTenantId);
  if (!can('user.create') || !can('user.manageScopes') || tenantId === '') return null;
  const goToImport = () => {
    void navigate({ to: '/users/import/$tenantId', params: { tenantId } });
  };

  return (
    <Button
      hierarchy="secondary"
      size="sm"
      iconLeading={<Icon name="upload-01" />}
      iconOnly={iconOnly}
      aria-label={iconOnly ? t('userImport.openAriaLabel') : undefined}
      onClick={goToImport}
    >
      {iconOnly ? undefined : t('userImport.open')}
    </Button>
  );
}

function HeaderActions({ iconOnly = false }: { iconOnly?: boolean }) {
  return (
    <div className={styles['header-actions']}>
      <ImportUsersButton iconOnly={iconOnly} />
      <AddUserButton iconOnly={iconOnly} />
    </div>
  );
}

/**
 * The Users list on `@smartsash/data-screen` (HS-869, the last slice of the plan's entity order and
 * the composite-key one: every row is addressed by `{ institutionId, userId }`).
 *
 * The page reads `GET /api/User/list`, the successor route, rather than `GET /api/User`: the old
 * route has no search, no sort and no `role`, and its `labId` is typed `number` while a laboratory
 * id is a string GUID — a filter that could never be called with a real value.
 * `searchScope`/`sortScope` are the factory's `'server'` default, and `usersQueryParams` turns the
 * URL into the query, shared with `AddUserButton`'s tenant lookup and `UsersFilter`'s cache read.
 */
export const usersScreen = defineDataScreen({
  entity: 'user',
  searchSchema: usersSearchSchema,
  useTranslate: useDataScreenTranslate,
  list: {
    query: (search) => pagedQuery<UserDTO>(getGetApiUserListQueryOptions(usersQueryParams(search))),
    columns,
    getRowId: (row) => row.id ?? '',
    mobileCard: (row) => <MobileCard row={row} />,
    titleKey: 'users.title',
    emptyState: {
      titleKey: 'users.empty.title',
      descriptionKey: 'users.empty.description',
      filteredTitleKey: 'list.emptyFiltered.title',
      filteredDescriptionKey: 'list.emptyFiltered.description',
    },
    selectable: true,
    rowActions: [{ id: 'row-actions', render: (row) => <RowActions row={row} /> }],
    headerAction: { id: 'add', permission: 'user.create', render: () => <HeaderActions /> },
    mobileHeaderAction: {
      id: 'add-mobile',
      permission: 'user.create',
      render: () => <HeaderActions iconOnly />,
    },
    filterSlot: (search) => <UsersFilter search={search} />,
  },
});
